import type {
    HttpPort,
    InlineReportResult,
    InlineUrlReportResult,
    IReportService,
    Json,
    PersistedReportResult,
    ReportActionDescriptor,
    RunReportOptions,
    RunReportResult,
} from "../contracts";

// ReportService — wrapper over foundation.base /api/reports/*. Engine-agnostic
// from the frontend's perspective; the backend routes to the right engine
// (DRE PDF, future ERE XLSX, …) by action_key.
export class ReportService implements IReportService {
    constructor(private readonly http: HttpPort) {}

    async listForContext(opts: {
        modelKey: string;
        recordId?: string;
        mode?: "form" | "list";
    }): Promise<ReportActionDescriptor[]> {
        const qs = new URLSearchParams({ model: opts.modelKey });
        if (opts.recordId) qs.set("record_id", opts.recordId);
        if (opts.mode) qs.set("mode", opts.mode);
        const res = await this.http.get<{ records?: ReportActionDescriptor[] }>(`/api/reports/_list?${qs.toString()}`);
        return res.records ?? [];
    }

    async run(actionKey: string, opts: RunReportOptions): Promise<RunReportResult> {
        const format = opts.format ?? "pdf";
        const persist = opts.persist ?? false;
        const body: Json = {
            target_record_uuids: opts.target_record_uuids,
            params: (opts.params ?? {}) as Json,
        };

        // Inline-rendered formats (pdf / html) route through the
        // token-based prepare + download endpoints so the new tab gets
        // a proper Content-Type response from the server — not a blob
        // URL. The blob path had a recurring "navigates same tab + looks
        // cached" failure mode in Chromium.
        if (!persist && (format === "pdf" || format === "html")) {
            const qsPrep = new URLSearchParams({ format });
            const prep = await this.http.post<Record<string, unknown>>(
                `/api/reports/${encodeURIComponent(actionKey)}/prepare?${qsPrep.toString()}`,
                body,
            );
            const token = String(prep["download_token"] ?? "");
            const result: InlineUrlReportResult = {
                persisted: false,
                kind: "inline_url",
                format: String(prep["format"] ?? format),
                content_type: String(prep["content_type"] ?? "application/octet-stream"),
                download_url: `/api/reports/download/${encodeURIComponent(token)}`,
                byte_count: Number(prep["byte_count"] ?? 0),
            };
            return result;
        }

        // Downloads (xlsx / docx / …) + persisted writes still use the
        // base64-in-JSON `/run` endpoint — the frontend turns the bytes
        // into a download blob.
        const qs = new URLSearchParams({ format, persist: String(persist) });
        const res = await this.http.post<Record<string, unknown>>(
            `/api/reports/${encodeURIComponent(actionKey)}/run?${qs.toString()}`,
            body,
        );
        if ("storage_document_uuid" in res) {
            const persisted: PersistedReportResult = {
                persisted: true,
                format: String(res["format"] ?? format),
                storage_document_uuid:
                    res["storage_document_uuid"] == null
                        ? null
                        : String(res["storage_document_uuid"]),
                byte_count: Number(res["byte_count"] ?? 0),
            };
            return persisted;
        }
        const inline: InlineReportResult = {
            persisted: false,
            format: String(res["format"] ?? format),
            content_type: String(res["content_type"] ?? "application/octet-stream"),
            bytes_base64: String(res["bytes_base64"] ?? ""),
            byte_count: Number(res["byte_count"] ?? 0),
        };
        return inline;
    }
}

// ── Client-side delivery for inline (base64) results ─────────────────────────

function base64ToBlob(b64: string, mimeType: string): Blob {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mimeType });
}

function defaultFilename(actionName: string, format: string): string {
    const safe = actionName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_+|_+$)/g, "") || "report";
    return `${safe}.${format}`;
}

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Open an absolute or relative URL in a new tab via a synthetic anchor
// click — same pattern as openBlobInNewTab but for actual HTTP URLs.
// Browsers consistently treat this as a user-gesture nav and route to
// `_blank`; `rel="noopener"` keeps the new tab from back-referencing
// the workspace.
function openUrlInNewTab(url: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

// Top-level delivery — branches on the result shape:
//   - `inline_url` (pdf / html via prepare+download)  → open the URL
//   - `persisted` (saved to storage.document)         → log only
//   - inline base64 (downloads: docx / xlsx / other)  → blob download
export function deliverReport(result: RunReportResult, actionName: string): void {
    if (result.persisted) {
        // eslint-disable-next-line no-console
        console.info("[ReportDelivery] Persisted to storage.document:",
            result.storage_document_uuid, `format=${result.format} bytes=${result.byte_count}`);
        return;
    }
    if ("kind" in result && result.kind === "inline_url") {
        openUrlInNewTab(result.download_url);
        return;
    }
    // Legacy base64 inline path — only reachable for non-(pdf/html)
    // formats that still go through `/run`. Treat as a download blob.
    // `bytes_base64` is the discriminant that narrows the union to
    // InlineReportResult (the persisted + inline_url members were returned
    // above; InlineReportResult has no `kind`, so the compound guard alone
    // doesn't narrow it for tsc).
    if ("bytes_base64" in result) {
        const blob = base64ToBlob(result.bytes_base64, result.content_type);
        const filename = defaultFilename(actionName, result.format);
        downloadBlob(blob, filename);
    }
}
